import { useState, useRef, useEffect } from 'react';
import { ArrowDownWideNarrow, ChevronDown, FileText, Check } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { formatInstituteDate } from '@/features/teacher/utils';
import { HomeworkTaskCard } from './HomeworkTaskCard';
import { getSubjectStyle } from './homeworkTheme';
import type { StudentWorkItem } from './workItem';

/** How the list can be ordered. Exported so the page can check one read from the address. */
export type HomeworkSort = 'DUE_EARLIEST' | 'DUE_LATEST' | 'ASSIGNED_NEWEST' | 'SUBJECT_AZ';

export const HOMEWORK_SORT_OPTIONS: { value: HomeworkSort; label: string }[] = [
  { value: 'DUE_EARLIEST', label: 'Due Date (Earliest)' },
  { value: 'DUE_LATEST', label: 'Due Date (Latest)' },
  { value: 'ASSIGNED_NEWEST', label: 'Recently Assigned' },
  { value: 'SUBJECT_AZ', label: 'Subject (A-Z)' },
];

interface HomeworkRosterSectionProps {
  upcomingList: StudentWorkItem[];
  overdueList: StudentWorkItem[];
  completedList: StudentWorkItem[];
  isCompletedCategory?: boolean;
  isPastDate?: boolean;
  showKindLabel?: boolean;
  /** How much work the open tab holds before the subject and date filters. */
  totalHomeworkCount: number;
  /** What the open tab is called, for the empty state — "homework", "activities". */
  emptyNoun: string;
  sortBy: HomeworkSort;
  onSortChange: (sort: HomeworkSort) => void;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  showCompleted?: boolean;
  onToggleCompleted?: () => void;
  uploadingKey: string | null;
  onDownloadWorksheet: (work: StudentWorkItem) => void;
  onViewDetails: (work: StudentWorkItem) => void;
  onOpenRepoModal: (work: StudentWorkItem) => void;
  onHandIn: (work: StudentWorkItem) => void;
}

export function HomeworkRosterSection({
  upcomingList,
  overdueList,
  completedList,
  isCompletedCategory = false,
  isPastDate = false,
  showKindLabel = false,
  totalHomeworkCount,
  emptyNoun,
  sortBy,
  onSortChange,
  isLoading,
  error,
  onRetry,
  uploadingKey,
  onDownloadWorksheet,
  onViewDetails,
  onOpenRepoModal,
  onHandIn,
}: HomeworkRosterSectionProps) {
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="lg:col-span-8 flex flex-col gap-4">
      {/* Section Toolbar: Title + Subject & Sort Dropdowns */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h3 className="text-base font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
          {isCompletedCategory ? (
            <span>Completed ({completedList.length})</span>
          ) : isPastDate ? (
            overdueList.length > 0 && upcomingList.length === 0 ? (
              <span className="text-rose-600 dark:text-rose-400">Overdue ({overdueList.length})</span>
            ) : (
              <span>Tasks ({overdueList.length + upcomingList.length})</span>
            )
          ) : (
            <span>Upcoming ({upcomingList.length})</span>
          )}
        </h3>

        <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
          {/* Sort Dropdown */}
          <div ref={sortRef} className="relative flex-1 sm:flex-none">
            <button
              type="button"
              onClick={() => {
                setIsSortOpen((prev) => !prev);
              }}
              className="flex items-center justify-between gap-2.5 bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 rounded-xl px-4 py-2 text-xs font-semibold shadow-2xs text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-white/20 transition-colors cursor-pointer w-full sm:w-auto"
            >
              <div className="flex items-center gap-2">
                <ArrowDownWideNarrow className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                <span className="text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-200 whitespace-nowrap">
                  {HOMEWORK_SORT_OPTIONS.find((opt) => opt.value === sortBy)?.label ?? 'Due Date (Earliest)'}
                </span>
              </div>
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 shrink-0 ${isSortOpen ? 'rotate-180' : ''}`} />
            </button>

            {isSortOpen && (
              <div className="absolute right-0 top-full mt-1.5 z-50 min-w-[195px] bg-white dark:bg-[#1b1d34] border border-slate-200 dark:border-white/10 rounded-2xl shadow-xl shadow-slate-900/10 dark:shadow-black/50 p-1.5 flex flex-col gap-0.5">
                {HOMEWORK_SORT_OPTIONS.map((opt) => {
                  const isSelected = sortBy === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        onSortChange(opt.value);
                        setIsSortOpen(false);
                      }}
                      className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-colors text-left cursor-pointer ${
                        isSelected
                          ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'
                      }`}
                    >
                      <span className="truncate">{opt.label}</span>
                      {isSelected && <Check className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400 shrink-0 ml-2" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-6">
          <SkeletonLines count={6} />
        </div>
      )}

      {/* Error State */}
      {!isLoading && error && (
        <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-6 flex flex-col items-start gap-3">
          <Alert tone="critical" title="Could not load your homework">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}

      {/* COMPLETED CARDS LIST (WHEN ON COMPLETED TAB) */}
      {isCompletedCategory && !isLoading && !error && completedList.length > 0 && (
        <div className="flex flex-col gap-3">
          {completedList.map((hw) => {
            const theme = getSubjectStyle(hw.subject.name);
            return (
              <div
                key={hw.key}
                className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-emerald-200/60 dark:border-emerald-900/30 p-4 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-3 opacity-90 hover:opacity-100 transition-opacity"
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50 shrink-0">
                    <Check className="h-5 w-5 stroke-[2.5]" />
                  </div>
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs font-bold">
                      <span className={`uppercase ${theme.textAccent}`}>{hw.subject.name}</span>
                      <span>•</span>
                      <span className="text-slate-500 dark:text-slate-400">
                        Handed in: {formatInstituteDate(hw.submission?.handedInOn ?? '')}
                      </span>
                    </div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">{hw.title}</h4>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end md:self-center shrink-0">
                  <button
                    type="button"
                    onClick={() => onViewDetails(hw)}
                    className="px-4 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 dark:bg-[#1f2240] text-indigo-700 dark:text-indigo-300 border border-slate-200 dark:border-indigo-800/50 text-sm font-bold transition-all cursor-pointer"
                  >
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* COMPLETED EMPTY STATE */}
      {isCompletedCategory && !isLoading && !error && completedList.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-white/10 bg-white dark:bg-[#1b1d34] p-10 text-center">
          <Check className="mx-auto h-10 w-10 text-slate-400 dark:text-slate-600" />
          <h4 className="mt-3 font-bold text-slate-800 dark:text-slate-200 text-base">
            No completed tasks
          </h4>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 font-medium">
            Tasks you hand in will appear here.
          </p>
        </div>
      )}

      {/* UPCOMING CARDS LIST (ONLY WHEN NOT PAST DATE) */}
      {!isCompletedCategory && !isPastDate && !isLoading && !error && upcomingList.length > 0 && (
        <div className="flex flex-col gap-3.5">
          {upcomingList.map((hw, idx) => (
            <HomeworkTaskCard
              key={hw.key}
              homework={hw}
              isFirst={idx === 0}
              isOverdue={false}
              showKindLabel={showKindLabel}
              uploadingKey={uploadingKey}
              onDownloadWorksheet={onDownloadWorksheet}
              onViewDetails={onViewDetails}
              onOpenRepoModal={onOpenRepoModal}
              onHandIn={onHandIn}
            />
          ))}
        </div>
      )}

      {/* UPCOMING EMPTY FALLBACK (WHEN OVERDUE ITEMS EXIST, ONLY WHEN NOT PAST DATE) */}
      {!isCompletedCategory && !isPastDate && !isLoading && !error && upcomingList.length === 0 && overdueList.length > 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300/80 dark:border-white/10 bg-white/60 dark:bg-[#1b1d34]/60 py-6 px-4 text-center">
          <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-slate-500 mb-1.5">
            <FileText className="h-4 w-4" />
          </div>
          <h4 className="font-bold text-slate-700 dark:text-slate-200 text-sm">
            No upcoming tasks
          </h4>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 font-medium">
            You have no upcoming tasks right now.
          </p>
        </div>
      )}

      {/* PAST DATE TASKS LIST (IF ANY TASKS ASSIGNED ON THIS PAST DATE) */}
      {!isCompletedCategory && isPastDate && !isLoading && !error && upcomingList.length > 0 && (
        <div className="flex flex-col gap-3.5">
          {upcomingList.map((hw, idx) => (
            <HomeworkTaskCard
              key={hw.key}
              homework={hw}
              isFirst={idx === 0}
              isOverdue={false}
              isPastDate={true}
              showKindLabel={showKindLabel}
              uploadingKey={uploadingKey}
              onDownloadWorksheet={onDownloadWorksheet}
              onViewDetails={onViewDetails}
              onOpenRepoModal={onOpenRepoModal}
              onHandIn={onHandIn}
            />
          ))}
        </div>
      )}

      {/* OVERDUE SECTION (IF OVERDUE ITEMS EXIST) */}
      {!isCompletedCategory && !isLoading && !error && overdueList.length > 0 && (
        <div className="flex flex-col gap-3.5">
          {(!isPastDate || upcomingList.length > 0) && (
            <h3 className="text-base font-bold text-rose-600 dark:text-rose-400 tracking-tight flex items-center gap-2">
              <span>Overdue ({overdueList.length})</span>
            </h3>
          )}

          {overdueList.map((hw) => (
            <HomeworkTaskCard
              key={hw.key}
              homework={hw}
              isOverdue={true}
              isPastDate={isPastDate}
              showKindLabel={showKindLabel}
              uploadingKey={uploadingKey}
              onDownloadWorksheet={onDownloadWorksheet}
              onViewDetails={onViewDetails}
              onOpenRepoModal={onOpenRepoModal}
              onHandIn={onHandIn}
            />
          ))}
        </div>
      )}

      {/* EMPTY STATE */}
      {!isCompletedCategory && !isLoading && !error && upcomingList.length === 0 && overdueList.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-white/10 bg-white dark:bg-[#1b1d34] p-10 text-center">
          <FileText className="mx-auto h-10 w-10 text-slate-400 dark:text-slate-600" />
          <h4 className="mt-3 font-bold text-slate-800 dark:text-slate-200 text-base">
            {totalHomeworkCount === 0 ? `No ${emptyNoun} set for your class yet` : 'Nothing matches your selected filters'}
          </h4>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 font-medium">
            {totalHomeworkCount === 0
              ? 'Work your teachers set appears here as soon as they do.'
              : 'Try clearing your subject or date filter.'}
          </p>
        </div>
      )}

      {/* Time Zone Note Footer */}
      <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium mt-1">
        <span className="h-4 w-4 rounded-full border border-slate-400 flex items-center justify-center text-[10px] font-bold">
          i
        </span>
        <span>All times shown are in your school time zone.</span>
      </div>
    </div>
  );
}
